/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
 * SOLWE: request-for-quote modal - product/SKU auto-fill.
 * CUSTOMIZATIONS.md "Request-for-quote block on storefront".
 *
 * Auto-fills a matching field inside the Shopify Forms embed a
 * merchant drops into the quote-request modal with the product/SKU
 * that opened it, since the modal has no product context of its own -
 * only the trigger button that opened it does (data-product-title /
 * data-product-sku). Two modal instances exist, both matched by the
 * "quote-request-modal" prefix on their id:
 *  - #quote-request-modal - one shared, site-wide instance, its own
 *    section (sections/quote-request-modal.liquid), registered in the
 *    four product JSON templates (product.json and its coming-soon /
 *    countdown / preorder variants) so it only renders on product
 *    pages, where sections/main-product.liquid's trigger lives.
 *  - #quote-request-modal-{{ section.id }} - one per Featured Product
 *    section instance, self-contained inside that section's own @app
 *    block (sections/featured-product.liquid), since a Featured
 *    Product section can be placed on any page/template and there's
 *    no way to guarantee a separate modal section is also added there.
 *
 * IF the embed's shadow root is open (JS-reachable). Confirmed open
 * live on this store on 2026-09-03, against an existing, unrelated
 * Forms embed (<shopify-forms-embed>, shadowRoot.mode === 'open'); a
 * programmatic value + dispatched input/change events persisted
 * through a blur and a 1.2s delay, so the framework's own state
 * accepted it, not just the DOM attribute. Not re-verified against a
 * real Flow run - Kerttu should submit one real test entry after
 * setting up the form (see CUSTOMIZATIONS.md) and confirm the value
 * lands in the submission.
 *
 * There is no visible fallback readout in the modal itself (removed
 * at request) - if the shadow root is ever unreachable (on this store,
 * or after a future Shopify Forms update), this quietly does nothing
 * and the submission carries no product/SKU context at all. Flagged,
 * not solved - see CUSTOMIZATIONS.md.
 *
 * FORM_FIELD_LABEL below MUST match, exactly, the label Kerttu gives
 * the product/SKU field when she builds the quote-request form in the
 * Shopify Forms app admin. The default here reuses the label already
 * live on this store's own "myymälään toimitus" -style form
 * ("Tuote / Tuotekoodi") for consistency - change this constant if she
 * uses different wording.
 *
 * The field is also set read-only and hidden from the customer here,
 * in code - Shopify Forms' own field settings have no read-only
 * option (checked directly in the Forms app admin). Filling then
 * hiding, rather than hiding first, means the value still exists and
 * still submits with the form; only the customer-facing input is
 * gone, so there's nothing left for them to accidentally edit or
 * clear before sending.
 */
(() => {
  const QUOTE_MODAL_ID_PREFIX = 'quote-request-modal';
  const FORM_FIELD_LABEL = 'Tuote / Tuotekoodi';
  const FORM_FIELD_WATCH_TIMEOUT = 10000;

  /**
   * Sets a text input's value the way a real user typing would, so
   * frameworks that track their own controlled state (as the Shopify
   * Forms embed does) pick up the change, not just the DOM attribute.
   * @param {HTMLInputElement} input - Target input element.
   * @param {string} value - Value to set.
   */
  function setNativeInputValue(input, value) {
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeSetter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Hides a filled field from the customer and locks it against
   * further edits. Hides the field's shared wrapper with its label
   * when one exists (so no empty label-shaped gap is left behind);
   * otherwise hides the input and label individually.
   * @param {HTMLInputElement} input - The field to hide/lock.
   * @param {?HTMLLabelElement} label - That field's <label>, if found.
   */
  function hideAndLockField(input, label) {
    input.readOnly = true;

    const sharedWrapper = label && label.parentElement === input.parentElement
      ? input.parentElement
      : null;

    if (sharedWrapper) {
      sharedWrapper.style.display = 'none';
    } else {
      input.style.display = 'none';
      if (label) label.style.display = 'none';
    }
  }

  /**
   * Finds the product/SKU field inside a Shopify Forms embed's shadow
   * root by its rendered label text (field ids are generated and not
   * stable), fills it, then hides it from the customer and marks it
   * read-only (see the block comment above this IIFE for why).
   * @param {ShadowRoot} shadowRoot - The embed's open shadow root.
   * @param {string} value - Product title + SKU to fill in.
   * @returns {boolean} Whether a matching field was found and filled.
   */
  function fillFormsField(shadowRoot, value) {
    const textInputs = Array.from(shadowRoot.querySelectorAll('input[type="text"]'));
    const target = textInputs.find((input) => {
      if (!input.id) return false;
      const label = shadowRoot.querySelector(`label[for="${input.id}"]`);
      return label && label.textContent.trim() === FORM_FIELD_LABEL;
    });

    if (!target) return false;

    setNativeInputValue(target, value);
    hideAndLockField(target, shadowRoot.querySelector(`label[for="${target.id}"]`));
    return true;
  }

  /**
   * Watches the modal for a Shopify Forms embed to render (it may not
   * exist yet on the modal's first open, if the embed script is still
   * loading), then fills the product/SKU field once its shadow root is
   * reachable. Gives up quietly after FORM_FIELD_WATCH_TIMEOUT.
   * @param {Element} modal - The quote-request modal-dialog element
   *   (the PDP's shared #quote-request-modal, or one Featured Product
   *   section's own #quote-request-modal-{section.id} instance).
   * @param {string} value - Product title + SKU to fill in.
   */
  function watchForFormsEmbed(modal, value) {
    const existingEmbed = modal.querySelector('shopify-forms-embed');
    if (existingEmbed && existingEmbed.shadowRoot) {
      fillFormsField(existingEmbed.shadowRoot, value);
      return;
    }

    const observer = new MutationObserver(() => {
      const embed = modal.querySelector('shopify-forms-embed');
      if (embed && embed.shadowRoot && fillFormsField(embed.shadowRoot, value)) {
        observer.disconnect();
      }
    });
    observer.observe(modal, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), FORM_FIELD_WATCH_TIMEOUT);
  }

  document.addEventListener('click', (evt) => {
    // Matches both the PDP's fixed "quote-request-modal" id and each
    // Featured Product section's own "quote-request-modal-{{ section.id }}"
    // instance (see sections/main-product.liquid and
    // sections/featured-product.liquid for the two trigger sources).
    const opener = evt.target.closest(`[data-modal^="${QUOTE_MODAL_ID_PREFIX}"]`);
    if (!opener) return;

    // Falls through safely (does nothing) if a merchant hasn't added the
    // quote-request app block yet - document.getElementById returns null
    // rather than throwing, and modal-opener's own click handler
    // (assets/main.js) already guards the same way before calling .open().
    const modal = document.getElementById(opener.dataset.modal);
    if (!modal) return;

    const title = opener.dataset.productTitle || '';
    const sku = opener.dataset.productSku || '';

    if (title) {
      const formValue = sku ? `${title} (Tuotenumero ${sku})` : title;
      watchForFormsEmbed(modal, formValue);
    }
  });
})();

/**
 * SOLWE: Svea part payment calculator block.
 * CUSTOMIZATIONS.md "Svea part payment calculator block".
 *
 * snippets/svea-part-payment.liquid renders the widget element Svea's
 * partPayment.js looks for (.svea-pp-widget-part-payment + data-*);
 * this loads that script once and keeps the widget's price equal to
 * the selected variant's price x quantity, which is what Svea's own
 * "dynamic script" instructions do for Shopify, but driven by the
 * theme's on:variant:change event instead of og:price:amount (which
 * only ever holds the variant the page was rendered with).
 *
 * The script is injected here rather than rendered by the block, so
 * two blocks on one page cannot load it twice (it registers document
 * listeners and window.SveaPartPayment each time it runs), and so a
 * block added in the theme editor works without a page reload: HTML
 * the editor swaps in does not execute <script> tags.
 *
 * Svea's "svea-partpayment-calculator-update-price" event updates
 * every widget on the page, not one. Fine while the block exists only
 * in the product section, which renders one product per page.
 */
(() => {
  const SVEA_SCRIPT_SRC = 'https://payments.maksuturva.fi/tools/partpayment/partPayment.js';
  const WRAPPER_SELECTOR = '.js-svea-part-payment';
  let scriptRequested = false;

  /**
   * Loads partPayment.js once, or re-scans for widgets if it is already loaded.
   * Injected after DOMContentLoaded, so Svea's own DOMContentLoaded init never
   * runs; its "svea-partpayment-calculator" event is the documented way in.
   */
  function initSvea() {
    if (!document.querySelector(WRAPPER_SELECTOR)) return;

    if (window.SveaPartPayment) {
      window.SveaPartPayment.initializeWidgets();
      return;
    }

    if (scriptRequested) return;
    scriptRequested = true;

    const script = document.createElement('script');
    script.src = SVEA_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      document.dispatchEvent(new CustomEvent('svea-partpayment-calculator'));
    };
    document.body.appendChild(script);
  }

  /**
   * Returns the Svea wrappers belonging to the product section an event came from.
   * @param {Element} target - Event target.
   * @returns {Element[]}
   */
  function wrappersFor(target) {
    const product = target.closest('.js-product');
    if (!product) return [];

    return Array.from(document.querySelectorAll(WRAPPER_SELECTOR)).filter((wrapper) => {
      const own = wrapper.closest('.js-product');
      return own && own.dataset.sectionId === product.dataset.sectionId;
    });
  }

  /**
   * Whether a price in cents is inside the block's min/max (inclusive, -1 = no limit).
   * Same rule as snippets/svea-part-payment.liquid.
   * @param {Element} wrapper - .js-svea-part-payment element.
   * @param {number} cents - Variant price x quantity, in cents.
   * @returns {boolean}
   */
  function inRange(wrapper, cents) {
    const min = parseInt(wrapper.dataset.minPrice, 10);
    const max = parseInt(wrapper.dataset.maxPrice, 10);

    if (cents <= 0) return false;
    if (min >= 0 && cents < min) return false;
    if (max >= 0 && cents > max) return false;
    return true;
  }

  /**
   * Recalculates a wrapper's price from its unit price and the section's quantity
   * input, and tells Svea. Hides the block while the price is 0 or out of range.
   * @param {Element} wrapper - .js-svea-part-payment element.
   */
  function updatePrice(wrapper) {
    const widget = wrapper.querySelector('.svea-pp-widget-part-payment');
    const qtyInput = wrapper.closest('.js-product').querySelector('[name="quantity"]');
    const qty = Math.max(parseInt(qtyInput ? qtyInput.value : 1, 10) || 1, 1);
    const unitPrice = parseInt(wrapper.dataset.unitPrice, 10) || 0;
    const cents = unitPrice * qty;
    const price = (cents / 100).toFixed(2);
    const visible = inRange(wrapper, cents);

    wrapper.hidden = !visible;
    widget.setAttribute('data-price', price);

    if (window.SveaPartPayment && visible) {
      document.dispatchEvent(new CustomEvent('svea-partpayment-calculator-update-price', {
        detail: { price }
      }));
    }
  }

  document.addEventListener('on:variant:change', (evt) => {
    const { variant } = evt.detail;

    wrappersFor(evt.target).forEach((wrapper) => {
      // No variant = an option combination that does not exist.
      wrapper.dataset.unitPrice = variant ? variant.price : 0;
      updatePrice(wrapper);
    });
  });

  document.addEventListener('change', (evt) => {
    if (!evt.target.matches('[name="quantity"]')) return;
    wrappersFor(evt.target).forEach(updatePrice);
  });

  document.addEventListener('shopify:section:load', initSvea);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSvea);
  } else {
    initSvea();
  }
})();

/**
 * SOLWE: Svea payment methods banner in the footer.
 * CUSTOMIZATIONS.md "Svea payment methods in the footer".
 *
 * snippets/svea-payment-banner.liquid renders the element Svea's banner.js
 * looks for (.maksuturva-banner + data-*); this loads that script once.
 * Injected here for the same reasons as the part payment calculator above:
 * one load per page, and a banner switched on in the theme editor works without a
 * page reload.
 *
 * banner.js only starts on DOMContentLoaded or its "maksuturva-load-banner"
 * event, and ignores the event once it has run, so a re-render calls
 * Maksuturva.initializeBanners() instead. That function removes every banner
 * it made before with parentNode.removeChild, which throws for one the editor
 * has already swapped out, so disconnected ones are dropped first.
 */
(() => {
  const BANNER_SCRIPT_SRC = 'https://payments.maksuturva.fi/pmt/js/ext/banner/banner.js';
  const BANNER_SELECTOR = '.maksuturva-banner';
  let scriptRequested = false;

  /**
   * Same narrow-layout class banner.js sets on first load and on resize.
   */
  function markNarrowBanners() {
    window.Maksuturva.banners.forEach((outer) => {
      outer.classList.toggle('maksuturva---banner-container-mobile', outer.offsetWidth < 480);
    });
  }

  function initBanner() {
    if (!document.querySelector(BANNER_SELECTOR)) return;

    if (window.Maksuturva && window.Maksuturva.initialLoadDone) {
      window.Maksuturva.banners = window.Maksuturva.banners.filter((outer) => outer.isConnected);
      window.Maksuturva.initializeBanners(markNarrowBanners);
      return;
    }

    if (scriptRequested) return;
    scriptRequested = true;

    const script = document.createElement('script');
    script.src = BANNER_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      document.dispatchEvent(new CustomEvent('maksuturva-load-banner'));
    };
    document.body.appendChild(script);
  }

  // Only when the re-rendered section holds a banner, so editing another
  // section does not rebuild it.
  document.addEventListener('shopify:section:load', (evt) => {
    if (evt.target.querySelector(BANNER_SELECTOR)) initBanner();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBanner);
  } else {
    initBanner();
  }
})();
